import React, { useState, useEffect } from 'react';

const CERT_TYPES = [
 'RSA Certificate',
 'White Card (Construction)',
 'Security Licence',
 'Food Handler Certificate',
 'First Aid Certificate',
 'Working With Children Check (WWCC)',
 'Forklift Licence (LF/LO)',
 'Police Check',
 'Responsible Manager Certificate',
 'Other'
];

const CERT_ICONS = {
 'RSA Certificate': '',
 'White Card (Construction)': '️',
 'Security Licence': '',
 'Food Handler Certificate': '️',
 'First Aid Certificate': '',
 'Working With Children Check (WWCC)': '',
 'Forklift Licence (LF/LO)': '',
 'Police Check': '',
 'Responsible Manager Certificate': '',
 'Other': ''
};

export default function CertificationsPage({ user }) {
 const [certs, setCerts] = useState([]);
 const [loading, setLoading] = useState(true);
 const [showForm, setShowForm] = useState(false);
 const [submitting, setSubmitting] = useState(false);
 const [form, setForm] = useState({
 cert_type: '', cert_number: '', issuing_body: '', issued_date: '', expiry_date: ''
 });
 const [deleteConfirm, setDeleteConfirm] = useState(null);

 const token = localStorage.getItem('token');

 useEffect(() => {
 fetchCerts();
 }, []);

 const fetchCerts = async () => {
 try {
 const res = await fetch('/api/certifications', { headers: { Authorization: `Bearer ${token}` } });
 const data = await res.json();
 setCerts(Array.isArray(data) ? data : []);
 } catch (e) { console.error(e); }
 setLoading(false);
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 setSubmitting(true);
 try {
 const res = await fetch('/api/certifications', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
 body: JSON.stringify(form)
 });
 const data = await res.json();
 if (res.ok) {
 setCerts(prev => [data, ...prev]);
 setShowForm(false);
 setForm({ cert_type: '', cert_number: '', issuing_body: '', issued_date: '', expiry_date: '' });
 }
 } catch (e) { console.error(e); }
 setSubmitting(false);
 };

 const handleDelete = async (id) => {
 try {
 await fetch(`/api/certifications/${id}`, {
 method: 'DELETE',
 headers: { Authorization: `Bearer ${token}` }
 });
 setCerts(prev => prev.filter(c => c.id !== id));
 setDeleteConfirm(null);
 } catch (e) { console.error(e); }
 };

 const getExpiryStatus = (expiryDate) => {
 if (!expiryDate) return null;
 const expiry = new Date(expiryDate);
 const now = new Date();
 const daysLeft = Math.floor((expiry - now) / (1000 * 60 * 60 * 24));
 if (daysLeft < 0) return { type: 'expired', label: 'Expired', days: daysLeft };
 if (daysLeft <= 30) return { type: 'warning', label: `Expires in ${daysLeft} days`, days: daysLeft };
 return null;
 };

 const getStatusBadge = (status) => {
 const badges = {
 verified: 'bg-brand-100 text-brand-800',
 pending: 'bg-yellow-100 text-yellow-800',
 expired: 'bg-red-100 text-red-800',
 rejected: 'bg-red-100 text-red-800'
 };
 const icons = { verified: '✓', pending: '⏳', expired: '✗', rejected: '✗' };
 return (
 <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badges[status] || badges.pending}`}>
 {icons[status] || '⏳'} {status?.charAt(0).toUpperCase() + status?.slice(1)}
 </span>
 );
 };

 if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div></div>;

 return (
 <div className="min-h-screen bg-neutral-50 py-8 px-4">
 <div className="max-w-3xl mx-auto">
 {/* Header */}
 <div className="flex items-center justify-between mb-6">
 <div className="flex items-center gap-3">
 <h1 className="text-2xl font-bold text-neutral-900">My Certifications</h1>
 <span className="bg-brand-600 text-white text-sm font-bold px-2.5 py-1 rounded-full">{certs.length}</span>
 </div>
 <button
 onClick={() => setShowForm(!showForm)}
 className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-4 py-2 rounded-lg transition"
 >
 + Add Certification
 </button>
 </div>

 {/* Info Box */}
 <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 mb-6">
 <p className="text-sm text-brand-800">
 <strong> Why add certifications?</strong> Employers can see your verified certs. Shifts that require RSA will show a match badge on your profile. Verified workers get more callbacks.
 </p>
 </div>

 {/* Add Form */}
 {showForm && (
 <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 mb-6">
 <h2 className="text-lg font-semibold text-neutral-900 mb-4">Add New Certification</h2>
 <form onSubmit={handleSubmit} className="space-y-4">
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1">Certification Type *</label>
 <select
 required
 value={form.cert_type}
 onChange={e => setForm(f => ({ ...f, cert_type: e.target.value }))}
 className="w-full border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
 >
 <option value="">Select type...</option>
 {CERT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
 </select>
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1">Certificate Number</label>
 <input
 type="text"
 value={form.cert_number}
 onChange={e => setForm(f => ({ ...f, cert_number: e.target.value }))}
 placeholder="e.g. RSA-2024-12345"
 className="w-full border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1">Issuing Body</label>
 <input
 type="text"
 value={form.issuing_body}
 onChange={e => setForm(f => ({ ...f, issuing_body: e.target.value }))}
 placeholder="e.g. OLGR NSW"
 className="w-full border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
 />
 </div>
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1">Issue Date</label>
 <input
 type="date"
 value={form.issued_date}
 onChange={e => setForm(f => ({ ...f, issued_date: e.target.value }))}
 className="w-full border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1">Expiry Date</label>
 <input
 type="date"
 value={form.expiry_date}
 onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))}
 className="w-full border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
 />
 </div>
 </div>
 <div className="flex gap-3 pt-2">
 <button
 type="submit"
 disabled={submitting}
 className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-6 py-2 rounded-lg transition disabled:opacity-50"
 >
 {submitting ? 'Saving...' : 'Save Certification'}
 </button>
 <button
 type="button"
 onClick={() => setShowForm(false)}
 className="bg-neutral-100 hover:bg-neutral-200 text-neutral-700 font-semibold px-6 py-2 rounded-lg transition"
 >
 Cancel
 </button>
 </div>
 </form>
 </div>
 )}

 {/* Certs List */}
 {certs.length === 0 ? (
 <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-12 text-center">
 <div className="text-5xl mb-4"></div>
 <p className="text-neutral-500 text-lg">No certifications yet</p>
 <p className="text-neutral-400 text-sm mt-1">Add your first certification to stand out to employers</p>
 </div>
 ) : (
 <div className="space-y-4">
 {certs.map(cert => {
 const expiryStatus = getExpiryStatus(cert.expiry_date);
 return (
 <div key={cert.id} className="bg-white rounded-xl shadow-sm border border-neutral-200 p-5">
 <div className="flex items-start justify-between">
 <div className="flex items-start gap-3">
 <span className="text-3xl">{CERT_ICONS[cert.cert_type] || ''}</span>
 <div>
 <div className="flex items-center gap-2 flex-wrap">
 <h3 className="font-semibold text-neutral-900">{cert.cert_type}</h3>
 {getStatusBadge(cert.status)}
 </div>
 {cert.cert_number && <p className="text-sm text-neutral-500 mt-0.5">#{cert.cert_number}</p>}
 {cert.issuing_body && <p className="text-sm text-neutral-500">Issued by {cert.issuing_body}</p>}
 {cert.expiry_date && (
 <p className="text-sm text-neutral-500">
 Expires: {new Date(cert.expiry_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
 </p>
 )}
 {expiryStatus && (
 <p className={`text-xs font-medium mt-1 ${expiryStatus.type === 'expired' ? 'text-red-600' : 'text-amber-600'}`}>
 ⚠️ {expiryStatus.label}
 </p>
 )}
 </div>
 </div>
 <div>
 {deleteConfirm === cert.id ? (
 <div className="flex gap-2">
 <button onClick={() => handleDelete(cert.id)} className="text-xs bg-red-600 text-white px-3 py-1 rounded-lg">Confirm</button>
 <button onClick={() => setDeleteConfirm(null)} className="text-xs bg-neutral-200 text-neutral-700 px-3 py-1 rounded-lg">Cancel</button>
 </div>
 ) : (
 <button onClick={() => setDeleteConfirm(cert.id)} className="text-neutral-400 hover:text-red-500 transition text-sm">️</button>
 )}
 </div>
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 </div>
 );
}
